/**
 * Login Page Component
 *
 * Provides user authentication functionality with:
 * - Email and password login form
 * - Form validation
 * - Multi-factor authentication support
 * - Error handling and feedback
 * - "Forgot password" option
 *
 * The component handles both regular login and transitions to OTP verification
 * when multi-factor authentication is enabled for the user.
 *
 * @module components/auth/login-page
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import Form from "react-bootstrap/Form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import TextField from "../reusable-form-fields/text-field";
import { faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { login } from "../../store/slices/auth";
import { showError, showSuccess } from "../../services/toast-service";
import { loginUser } from "../../services/api-declaration";
import { LoginFormSchema } from "../../services/validation-schema";
import AuthTemplate from "./auth-template";
import SubmitButton from "./submit-button";
import PasswordToggle from "../change-password/field-with-toggle";
import OTPLogin from "./otp-login";
import { MULTI_FACTOR_TYPES } from "../strings";
import { useTranslation } from "react-i18next";

/**
 * Login Page component that handles user authentication
 *
 * @returns {JSX.Element} The login form or OTP verification component
 */
const LoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Component state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldType, setFieldType] = useState("password"); // For password visibility toggle
  const [otpLoginData, setOTPLoginData] = useState({
    show: false, // Whether to show OTP screen
    authenticators: [] // Available MFA methods
  });

  /**
   * Handle login form submission
   * Manages the login flow including MFA transitions
   *
   * @param {Object} formValues - Form values from react-hook-form
   * @param {string} formValues.email - User email
   * @param {string} formValues.password - User password
   */
  const handleLogin = async (formValues) => {
    try {
      setIsSubmitting(true);
      // Call login API
      const { data } = await loginUser(formValues);
      const userData = data?.data?.userData;

      // Check if user has multi-factor authentication enabled
      if (
        userData.multiFactorEnabled.email &&
        userData.multiFactorEnabled.authenticator
      ) {
        // Both email and authenticator MFA enabled
        setOTPLoginData({
          show: true,
          authenticators: [] // Empty means show method selection
        });
        return;
      } else if (userData.multiFactorEnabled.email) {
        // Only email MFA enabled
        setOTPLoginData({
          show: true,
          authenticators: [{ text: "Email", type: MULTI_FACTOR_TYPES.EMAIL }]
        });
        return;
      }

      // No MFA, complete login
      dispatch(login(data));
      showSuccess(t("loginSuccessFull"));
    } catch (error) {
      console.log(error);
      // Show appropriate error message
      showError(error.response?.data?.message || t("toastMessage.wentWrong"));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Toggle password field visibility between text and password
   */
  const togglePasswordField = () =>
    setFieldType(fieldType === "password" ? "text" : "password");

  /**
   * Initialize react-hook-form with validation schema
   */
  const methods = useForm({
    defaultValues: { email: "", password: "" },
    resolver: yupResolver(LoginFormSchema) // Yup validation schema
  });

  // Destructure form methods for use in the component
  const {
    register, // Field registration
    handleSubmit, // Form submission handler
    getValues, // Get current form values
    formState: { errors } // Validation errors
  } = methods;
  /**
   * Render either the login form or OTP verification component
   * based on the authentication flow state
   */
  return (
    <FormProvider {...methods}>
      {otpLoginData.show ? (
        // Show OTP verification screen if MFA is enabled
        <OTPLogin
          email={getValues("email")}
          password={getValues("password")}
          authenticators={otpLoginData.authenticators}
        />
      ) : (
        // Standard login form
        <AuthTemplate title={t("common.login")}>
          <Form onSubmit={handleSubmit(handleLogin)}>
            {/* Email input field */}
            <TextField
              type="email"
              name="email"
              register={register}
              label={t("common.email")}
              placeholder={t("common.enterEmail")}
              errors={errors?.email}
            />

            {/* Password input field with visibility toggle */}
            <PasswordToggle
              label={t("password")}
              type={fieldType}
              name="password"
              error={errors?.password}
              toggleHandler={togglePasswordField}
              thinLabel
            />

            <hr />

            {/* Form actions: Submit and Forgot Password */}
            <div className="d-flex justify-content-between align-items-center">
              <SubmitButton
                btnText={t("common.login")}
                icon={faRightToBracket}
                isSubmitting={isSubmitting}
                spinnerBtnText={`${t("common.login")}...`}
              />
              <Link to="/forgot">
                <p className="m-0">{t("common.forgotPassword")}</p>
              </Link>
            </div>
          </Form>
        </AuthTemplate>
      )}
    </FormProvider>
  );
};
export default LoginPage;
